import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell } from '@fortawesome/free-solid-svg-icons';
import notificationService from '../../services/notificationService';
import { costCentreBellTarget } from '../../constants/costCentreBells';
import './NotificationBell.css';

/**
 * NotificationBell — global, always-visible notification access
 * (Jonas feedback 19.08: "we need more/easier access to notifications").
 *
 * The notifications page was buried two levels deep in the collapsed
 * sidebar. This fixed bell lives on every Layout screen: unread badge,
 * click for a dropdown of the latest unread notifications, mark-all-read,
 * and a jump to the full page.
 *
 * Polls /api/notifications/stats/ every 30s and listens for the existing
 * `notificationStatsChanged` window event (dispatched by the notification
 * screens after any state change) so the badge stays honest without a
 * second polling contract.
 */
const NotificationBell = () => {
  const history = useHistory();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);

  const refreshStats = useCallback(async () => {
    try {
      const stats = await notificationService.getStats();
      setUnread(stats?.unread ?? 0);
    } catch (e) {
      // Silent: the bell must never break a screen over a stats hiccup.
    }
  }, []);

  useEffect(() => {
    refreshStats();
    const interval = setInterval(refreshStats, 30000);
    window.addEventListener('notificationStatsChanged', refreshStats);
    return () => {
      clearInterval(interval);
      window.removeEventListener('notificationStatsChanged', refreshStats);
    };
  }, [refreshStats]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const toggleOpen = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      setLoading(true);
      try {
        const data = await notificationService.getNotifications({ status: 'unread' });
        const list = Array.isArray(data) ? data : data?.results || [];
        setItems(list.slice(0, 10));
      } catch (e) {
        setItems([]);
      } finally {
        setLoading(false);
      }
    }
  };

  const openNotification = async (n) => {
    try {
      await notificationService.markAsRead(n.id);
    } catch (e) {
      // best effort
    }
    window.dispatchEvent(new Event('notificationStatsChanged'));
    setOpen(false);
    // IMT only (divergence 07): a cost-centre bell opens its own screen directly.
    history.push(costCentreBellTarget(n) || '/settings/notifications');
  };

  const markAllRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setItems([]);
      window.dispatchEvent(new Event('notificationStatsChanged'));
    } catch (e) {
      // best effort
    }
  };

  return (
    <div className="hms-notification-bell" ref={containerRef}>
      <button
        type="button"
        className="hms-notification-bell-button"
        onClick={toggleOpen}
        aria-label={`Notifications${unread > 0 ? ` (${unread} unread)` : ''}`}
      >
        <FontAwesomeIcon icon={faBell} />
        {unread > 0 && (
          <span className="hms-notification-bell-badge">{unread > 99 ? '99+' : unread}</span>
        )}
      </button>

      {open && (
        <div className="hms-notification-bell-dropdown">
          <div className="hms-notification-bell-dropdown-header">
            <span>Notifications</span>
            {unread > 0 && (
              <button type="button" onClick={markAllRead} className="hms-notification-bell-markall">
                Mark all read
              </button>
            )}
          </div>

          <div className="hms-notification-bell-list">
            {loading ? (
              <div className="hms-notification-bell-empty">Loading…</div>
            ) : items.length === 0 ? (
              <div className="hms-notification-bell-empty">No unread notifications</div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  className="hms-notification-bell-item"
                  onClick={() => openNotification(n)}
                >
                  <span className="hms-notification-bell-item-title">{n.title}</span>
                  {n.description && (
                    <span className="hms-notification-bell-item-message">{n.description}</span>
                  )}
                </button>
              ))
            )}
          </div>

          <button
            type="button"
            className="hms-notification-bell-footer"
            onClick={() => {
              setOpen(false);
              history.push('/settings/notifications');
            }}
          >
            View all notifications
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
